import React from 'react';
import {
  Button, Radio, SelectionControlLabel, Typography
} from '@one-thd/sui-atomic-components';
import PropTypes from 'prop-types';
import { MobileAwareOverlay } from '../../../components/MobileAwareOverlay';
import './confirmAddressModal.scss';
import { AddressType } from './types';
import {
  IconWithRoundBorder
} from '../IconWithRoundBorder/IconWithRoundBorder';
import { formatAddress } from './common';
import { addAnalyticEvent } from '../../analytics/common';
import { confirmAddressOverlay } from '../../analytics/address';
import LinkButton from '../LinkButton';

const MOBILE_CONFIRM_BUTTON_TEXT = 'Confirm Verified';
const DESKTOP_CONFIRM_BUTTON_TEXT = 'Confirm';

export const ConfirmAddressModal = ({
  unsubscribeAddress, unsubscribeVerifiedAddress, confirmUseUnverified, isMobile, onClose,
  useVerifiedAddress, address, tryAgainButton
}) => {

  React.useEffect(() => {
    addAnalyticEvent(confirmAddressOverlay);
  }, []);

  const getConfirmButtonText = () => {
    return isMobile && useVerifiedAddress ? MOBILE_CONFIRM_BUTTON_TEXT : DESKTOP_CONFIRM_BUTTON_TEXT;
  };

  return (
    <MobileAwareOverlay
      modalClass="preferences-modal"
      onClose={onClose}
      open
    >
      <div className="sui-grid sui-gap-4 preferences-modal__content">
        <div className="sui-flex sui-items-center sui-gap-4">
          {!isMobile && (
            <IconWithRoundBorder
              iconSrc="https://assets.homedepot-static.com/images/v1/mailbox.svg"
              iconWidth="27.7px"
            />
          )}
          <Typography variant="h4">
            {useVerifiedAddress ? 'Confirm' : 'Verify'} Your Mailing Address
          </Typography>
        </div>
        <div>
          <Typography variant="body-base" weight="bold">
            The address entered
            {useVerifiedAddress ? ' matched the ' : ' did not match a '}
            verified address provided by United Parcel Service (UPS).
            If you have received information from us in the past, enter your address
            exactly as it appears on the correspondence. Any errors may prevent removal
            from our mailing list.{' '}
          </Typography>
          {!isMobile && (<br />)}
          <Typography variant="body-base">
            Please confirm the correct address, use the entered address or{' '}
            <LinkButton onClick={tryAgainButton} testId="verified-try-again">
              try again
            </LinkButton>
            .
          </Typography>
        </div>

        <Typography variant="body-base" weight="bold">
          {useVerifiedAddress ? 'Verified' : 'Entered'} Mailing Address
        </Typography>
        <div className="sui-border-solid sui-border-1 sui-border-primary sui-rounded-base sui-p-4">
          <SelectionControlLabel value="verified" label={formatAddress(address)}>
            <Radio
              id="verifiedAddress"
              checked
              value="Verified"
              onChange={() => {}}
            />
          </SelectionControlLabel>
        </div>

        <hr className="hr-line" />
        <div className="sui-grid sui-grid-cols-12 sui-gap-4">
          {!isMobile && <div className={useVerifiedAddress ? 'sui-col-span-4' : 'sui-col-span-8'} />}
          {useVerifiedAddress && (
            <div className="sui-col-span-6 md:sui-col-span-4">
              <Button
                aria-label="unverified"
                data-testid="unverified"
                onClick={confirmUseUnverified}
                variant="secondary"
                fullWidth
              >
                Use Unverified{isMobile && ' Address'}
              </Button>
            </div>
          )}
          <div className={`${useVerifiedAddress ? 'sui-col-span-6' : 'sui-col-span-12'} md:sui-col-span-4`}>
            <Button
              onClick={useVerifiedAddress ? unsubscribeVerifiedAddress : unsubscribeAddress}
              fullWidth
            >
              {getConfirmButtonText()}
            </Button>
          </div>
        </div>
      </div>
    </MobileAwareOverlay>
  );
};

ConfirmAddressModal.propTypes = {
  tryAgainButton: PropTypes.func.isRequired,
  address: AddressType.isRequired,
  onClose: PropTypes.func.isRequired,
  useVerifiedAddress: PropTypes.bool.isRequired,
  isMobile: PropTypes.bool,
  confirmUseUnverified: PropTypes.func.isRequired,
  unsubscribeVerifiedAddress: PropTypes.func.isRequired,
  unsubscribeAddress: PropTypes.func.isRequired,
};

ConfirmAddressModal.defaultProps = {
  isMobile: false,
};
